import { Marker } from './iMediaPlayer';

export enum ContentType {
  Text = 'TEXT',
  Quiz = 'QUIZ',
  CodeLab = 'CODE_LAB',
  Video = 'VIDEO',
  InteractiveImage = 'INTERACTIVE_IMAGE',
  TextSurvey = 'TEXT_SURVEY',
  SelectSurvey = 'SELECT_SURVEY',
  Pdf = 'PDF',
}

export interface BaseContentData {
  headline: string;
  id: string;
  order: number;
  skillId: string;
  sectionId: string;
}

export interface TextContentSpecificData {
  text: string;
}

export type TextContentData = TextContentSpecificData & {
  type: ContentType.Text;
} & BaseContentData;

export interface CodeLabContentProps {
  identifier: string;
}

export type CodeLabContentData = CodeLabContentProps & {
  type: ContentType.CodeLab;
} & BaseContentData;

interface VideoContentSpecificBaseData {
  markers: Marker[];
  description: string;
}

interface UrlVideoData extends VideoContentSpecificBaseData {
  url: string;
  storagePath?: never;
}

interface StoragePathVideoData extends VideoContentSpecificBaseData {
  url?: never;
  storagePath: string;
}

export type VideoContentSpecificData = UrlVideoData | StoragePathVideoData;
export type VideoContentData = VideoContentSpecificData & {
  type: ContentType.Video;
} & BaseContentData;

export type ImageArea = { key: string; placement: [number, number]; text: string };

export interface InteractiveImageSpecificProps {
  imageUrl: string;
  areas: ImageArea[];
}

export type InteractiveImageContentData = InteractiveImageSpecificProps & {
  type: ContentType.InteractiveImage;
} & BaseContentData;

export interface PdfContentSpecificData {
  storagePath: string;
}

export type PdfContentData = PdfContentSpecificData & {
  type: ContentType.Pdf;
} & BaseContentData;

/**
 * Wrapper for quiz answer alternatives which adds a key needed by React when rendered in a list.
 */
export interface AnswerAlternativeData {
  answer: string;
  feedback: string;
  correct: boolean;
  /**
   * Intended to be used as key when rendered as a list in React. Generate with uuidv4().
   */
  key: string;
}

export interface QuizContentSpecificData {
  question: string; // The quiz question or statement.
  description: string;
  answerAlternatives: AnswerAlternativeData[];
}

export type QuizContentData = QuizContentSpecificData & {
  type: ContentType.Quiz;
} & BaseContentData;

export interface TextSurveyContentSpecificData {
  description: string;
}

export type TextSurveyContentData = TextSurveyContentSpecificData & {
  type: ContentType.TextSurvey;
} & BaseContentData;

export type SurveyAlternativeData = {
  key: string;
  text: string;
};

export enum SelectType {
  Single = 'SINGLE',
  Multiple = 'MULTIPLE',
}

export interface SelectSurveyContentSpecificData {
  selectType: SelectType;
  description: string;
  alternatives: SurveyAlternativeData[];
}

export type SelectSurveyContentData = SelectSurveyContentSpecificData & {
  type: ContentType.SelectSurvey;
} & BaseContentData;

export type ContentData =
  | CodeLabContentData
  | InteractiveImageContentData
  | PdfContentData
  | QuizContentData
  | SelectSurveyContentData
  | TextContentData
  | TextSurveyContentData
  | VideoContentData;

export type ContentSpecificData =
  | CodeLabContentProps
  | InteractiveImageSpecificProps
  | PdfContentSpecificData
  | QuizContentSpecificData
  | SelectSurveyContentSpecificData
  | TextContentSpecificData
  | TextSurveyContentSpecificData
  | VideoContentSpecificData;
